// ===== INVOICE UTILITIES =====

import { paymentTermsOptions } from './sampleData';

// Invoice status configuration
export const invoiceStatuses = {
  draft: { 
    label: 'Draft', 
    color: '#6b7280', 
    bg: '#f3f4f6',
    description: 'Invoice created but not sent'
  },
  sent: { 
    label: 'Sent', 
    color: '#3b82f6', 
    bg: '#eff6ff',
    description: 'Sent to client, awaiting payment'
  },
  pending: { 
    label: 'Pending', 
    color: '#b8926a', 
    bg: '#faf6f1',
    description: 'Sent to client, awaiting payment'
  },
  paid: { 
    label: 'Paid', 
    color: '#16a34a', 
    bg: '#f0fdf4',
    description: 'Payment received'
  },
  overdue: { 
    label: 'Overdue', 
    color: '#dc2626', 
    bg: '#fef2f2',
    description: 'Past due date'
  },
  cancelled: { 
    label: 'Cancelled', 
    color: '#9ca3af', 
    bg: '#f9fafb',
    description: 'Invoice cancelled'
  }
};

// Line item types
export const lineItemTypes = {
  service: { label: 'Cleaning Service', category: 'service' },
  addon: { label: 'Add-On', category: 'addon' },
  laundry: { label: 'Laundry Service', category: 'laundry' },
  tip: { label: 'Tip', category: 'tip' },
  discount: { label: 'Discount', category: 'adjustment' },
  cancellation_fee: { label: 'Cancellation Fee', category: 'fee' },
  late_fee: { label: 'Late Fee', category: 'fee' }
};

// Generate invoice number with format INV-YYYY-XXXX
let invoiceCounter = null;

export const generateInvoiceNumber = () => {
  const year = new Date().getFullYear();
  
  // Initialize counter with random starting number if not set
  if (invoiceCounter === null) {
    invoiceCounter = Math.floor(Math.random() * 9000) + 1000; // Random 4-digit start
  }
  
  invoiceCounter++;
  const paddedNumber = String(invoiceCounter).padStart(4, '0');
  
  return `INV-${year}-${paddedNumber}`;
};

// Calculate due date based on payment terms
export const calculateDueDate = (invoiceDate, paymentTerms) => {
  const date = new Date(invoiceDate);
  const terms = paymentTermsOptions.find(t => t.value === paymentTerms);
  const daysToAdd = terms?.days || 0;
  
  date.setDate(date.getDate() + daysToAdd);
  return date.toISOString().split('T')[0];
};

// Get payment terms label
export const getPaymentTermsLabel = (termsValue) => {
  const terms = paymentTermsOptions.find(t => t.value === termsValue);
  return terms?.label || 'Due Upon Receipt';
};

// Determine if a client is new (first clean) based on job data
export const isNewClientJob = (job) => {
  return job.isFirstClean === true;
};

// Create invoice from completed job
export const createInvoiceFromJob = (job, client, lineItems = []) => {
  const invoiceDate = new Date().toISOString().split('T')[0];
  const dueDate = calculateDueDate(invoiceDate, client.paymentTerms || 'due_on_receipt');

  // Build line items from job
  const jobLineItems = [];

  // Main service line item
  jobLineItems.push({
    id: `li_${Date.now()}_1`,
    type: 'service',
    description: `${getServiceLabel(job.serviceType)} - ${job.address}`,
    quantity: 1,
    unitPrice: job.price,
    total: job.price
  });

  // Add any laundry if it's a laundry job
  if (job.serviceType === 'laundry_pickup' && job.laundryWeight) {
    jobLineItems[0] = {
      id: `li_${Date.now()}_1`,
      type: 'laundry',
      description: `Laundry Service - ${job.laundryWeight} lbs @ $3.00/lb`,
      quantity: job.laundryWeight,
      unitPrice: 3.00,
      total: job.laundryWeight * 3.00
    };
  }

  // Add additional line items passed in
  const allLineItems = [...jobLineItems, ...lineItems];

  // Calculate totals
  const subtotal = allLineItems.reduce((sum, item) => sum + item.total, 0);
  const tax = 0; // No tax for cleaning services in VA
  const total = subtotal + tax;

  // New clients pay in full before their appointment (handled at booking time),
  // so completion-time invoices no longer auto-enable a partial deposit.
  const isFirstClean = isNewClientJob(job);
  const requestDeposit = false;
  const depositType = 'percent';
  const depositValue = 50;
  const depositAmount = 0;
  const depositDueDate = null;

  return {
    id: `inv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    invoiceNumber: generateInvoiceNumber(),
    clientId: client.id,
    clientName: `${client.firstName} ${client.lastName}`,
    clientEmail: client.accountingEmail || client.email,
    clientPhone: client.phone,
    jobId: job.id,
    jobDate: job.date,
    propertyAddress: job.fullAddress || job.address,
    status: 'draft',
    invoiceDate,
    dueDate,
    paymentTerms: client.paymentTerms || 'due_on_receipt',
    lineItems: allLineItems,
    subtotal,
    tax,
    total,
    // Deposit info (auto-set for new clients)
    requestDeposit,
    depositType: requestDeposit ? depositType : null,
    depositValue: requestDeposit ? depositValue : null,
    depositAmount,
    depositDueDate,
    depositPaid: false,
    isFirstCleanInvoice: isFirstClean,
    tipAmount: 0,
    amountPaid: 0,
    balance: total,
    // Stripe fields
    stripeInvoiceId: null,
    stripePaymentUrl: null,
    stripeCustomerId: client.stripeCustomerId || null,
    // Timestamps
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    sentAt: null,
    paidAt: null,
    stripePaidAt: null
  };
};

// Get service type label
const getServiceLabel = (serviceType) => {
  const labels = {
    weekly: 'Weekly Cleaning',
    biweekly: 'Bi-Weekly Cleaning',
    monthly: 'Monthly Cleaning',
    deep_clean: 'Deep Clean',
    move_in_out: 'Move In/Out Clean',
    one_time: 'One-Time Cleaning',
    laundry_pickup: 'Laundry Pickup'
  };
  return labels[serviceType] || 'Cleaning Service';
};

// Format currency
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount || 0);
};

// Check if invoice is overdue
export const isInvoiceOverdue = (invoice) => {
  if (invoice.status === 'paid' || invoice.status === 'cancelled') return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dueDate = new Date(invoice.dueDate);
  dueDate.setHours(0, 0, 0, 0);
  return today > dueDate;
};

// Get number of days invoice is overdue
export const getDaysOverdue = (invoice) => {
  if (!isInvoiceOverdue(invoice)) return 0;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dueDate = new Date(invoice.dueDate);
  dueDate.setHours(0, 0, 0, 0);
  const diffTime = today - dueDate;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

// Calculate tip split for team members
export const calculateTipSplit = (tipAmount, teamMembers) => {
  if (!teamMembers || teamMembers.length === 0 || !tipAmount) {
    return [];
  }
  
  const splitAmount = tipAmount / teamMembers.length;
  
  return teamMembers.map(member => ({
    employeeId: member.id || member.employeeId,
    employeeName: member.name || `${member.firstName} ${member.lastName || ''}`.trim(),
    amount: Math.round(splitAmount * 100) / 100 // Round to 2 decimal places
  }));
};

// Sample invoices for testing
export const sampleInvoices = [
  {
    id: 'inv1',
    invoiceNumber: 'INV-2026-3847',
    clientId: 'client1',
    clientName: 'Emily Patterson',
    clientEmail: 'emily.patterson@email.com',
    clientPhone: '(434) 555-1001',
    jobId: 'job8',
    jobDate: '2026-01-22',
    propertyAddress: '100 Cedar Court, Charlottesville, VA 22901',
    status: 'paid',
    invoiceDate: '2026-01-22',
    dueDate: '2026-01-22',
    paymentTerms: 'due_on_receipt',
    lineItems: [
      {
        id: 'li1',
        type: 'service',
        description: 'Bi-Weekly Cleaning - 100 Cedar Court',
        quantity: 1,
        unitPrice: 185,
        total: 185
      }
    ],
    subtotal: 185,
    tax: 0,
    total: 185,
    tipAmount: 20,
    amountPaid: 205,
    balance: 0,
    stripeInvoiceId: 'in_test123',
    stripePaymentUrl: null,
    createdAt: '2026-01-22T11:30:00Z',
    updatedAt: '2026-01-22T14:00:00Z',
    sentAt: '2026-01-22T11:35:00Z',
    paidAt: '2026-01-22T14:00:00Z',
    paymentMethod: 'card',
    tipSplit: [
      { employeeId: 'emp1', employeeName: 'Ashanti', amount: 10 },
      { employeeId: 'emp2', employeeName: 'Brandi', amount: 10 }
    ]
  },
  {
    id: 'inv2',
    invoiceNumber: 'INV-2026-3848',
    clientId: 'client3',
    clientName: 'Sarah Johnson',
    clientEmail: 'sarah.j@email.com',
    clientPhone: '(434) 555-1003',
    jobId: 'job4',
    jobDate: '2026-01-23',
    propertyAddress: '321 Pine Road, Crozet, VA 22932',
    status: 'pending',
    invoiceDate: '2026-01-23',
    dueDate: '2026-01-23',
    paymentTerms: 'due_on_receipt',
    lineItems: [
      {
        id: 'li2',
        type: 'service',
        description: 'Bi-Weekly Cleaning - 321 Pine Road',
        quantity: 1,
        unitPrice: 195,
        total: 195
      }
    ],
    subtotal: 195,
    tax: 0,
    total: 195,
    tipAmount: 0,
    amountPaid: 0,
    balance: 195,
    stripeInvoiceId: 'in_test456',
    stripePaymentUrl: 'https://invoice.stripe.com/i/test456',
    createdAt: '2026-01-23T11:45:00Z',
    updatedAt: '2026-01-23T11:50:00Z',
    sentAt: '2026-01-23T11:50:00Z',
    paidAt: null
  },
  {
    id: 'inv3',
    invoiceNumber: 'INV-2026-3849',
    clientId: 'client2',
    clientName: 'Michael Thompson',
    clientEmail: 'mthompson@blueridgepm.com',
    clientPhone: '(434) 555-1002',
    jobId: 'job9',
    jobDate: '2026-01-22',
    propertyAddress: '250 Oak Hill Drive, Charlottesville, VA 22903',
    status: 'draft',
    invoiceDate: '2026-01-22',
    dueDate: '2026-02-21',
    paymentTerms: 'net_30',
    lineItems: [
      {
        id: 'li3',
        type: 'service',
        description: 'Weekly Cleaning - 250 Oak Hill Drive',
        quantity: 1,
        unitPrice: 165,
        total: 165
      }
    ],
    subtotal: 165,
    tax: 0,
    total: 165,
    tipAmount: 0,
    amountPaid: 0,
    balance: 165,
    stripeInvoiceId: null,
    stripePaymentUrl: null,
    createdAt: '2026-01-22T15:45:00Z',
    updatedAt: '2026-01-22T15:45:00Z',
    sentAt: null,
    paidAt: null
  }
];
